import Manifest from '../core/manifest'
import Utils from '../utils/utils'
import Platform from '../core/platform'
import Storage from '../core/storage/storage'
import Account from '../core/account/account'

const FLUSH_INTERVAL_MS = 5000
const FLUSH_BATCH_LIMIT = 50
const HEARTBEAT_MS      = 30 * 1000
const PROGRESS_MS       = 30 * 1000

let API_BASE       = ''
let SESSION_ID     = ''
let VISITOR_UID    = ''
let queue          = []
let flushTimer     = null
let hbTimer        = null
let inited         = false
let currentPlayback = null
let progressTimer  = null
let lastOpenedCard  = null

function uuid(){
    if(typeof crypto !== 'undefined' && crypto.randomUUID){
        try { return crypto.randomUUID() } catch(e){}
    }
    let buf = new Array(16).fill(0).map(() => Math.floor(Math.random() * 256))
    buf[6] = (buf[6] & 0x0f) | 0x40
    buf[8] = (buf[8] & 0x3f) | 0x80
    // На старых Tizen/webOS нет ES2017 string-паддинга — паддим вручную (ES5-safe)
    let hex = buf.map(b => (b < 16 ? '0' : '') + b.toString(16)).join('')
    return hex.slice(0,8) + '-' + hex.slice(8,12) + '-' + hex.slice(12,16) + '-' + hex.slice(16,20) + '-' + hex.slice(20)
}

function platformName(){
    if(Platform.screen('tv')) return 'tv'
    if(Platform.screen('mobile')) return 'mobile'
    return 'desktop'
}

// Парсер User-Agent для извлечения OS / браузера / версий.
// Простая регекс-классификация, покрывает основные таргет-платформы (Windows,
// macOS, iOS, Android, Linux, Tizen, webOS) и браузеры (Chrome/Edge/Firefox/Safari/Opera).
function parseUserAgent(ua){
    let s = (ua || '').toLowerCase()
    let r = { browser: '', browserVersion: '', os: '', osVersion: '', device: '' }

    if(s.indexOf('windows nt') >= 0){
        r.os = 'Windows'
        let m = s.match(/windows nt (\d+\.\d+)/); if(m) r.osVersion = m[1]
    }
    else if(s.indexOf('iphone') >= 0 || s.indexOf('ipad') >= 0 || s.indexOf('ipod') >= 0){
        r.os = 'iOS'
        let m = s.match(/os (\d+[._]\d+(?:[._]\d+)?)/); if(m) r.osVersion = m[1].replace(/_/g, '.')
        r.device = s.indexOf('ipad') >= 0 ? 'iPad' : (s.indexOf('ipod') >= 0 ? 'iPod' : 'iPhone')
    }
    else if(s.indexOf('mac os x') >= 0 || s.indexOf('macintosh') >= 0){
        r.os = 'macOS'
        let m = s.match(/mac os x (\d+[._]\d+(?:[._]\d+)?)/); if(m) r.osVersion = m[1].replace(/_/g, '.')
    }
    else if(s.indexOf('android') >= 0){
        r.os = 'Android'
        let m = s.match(/android (\d+(?:\.\d+)*)/); if(m) r.osVersion = m[1]
    }
    else if(s.indexOf('tizen') >= 0){
        r.os = 'Tizen'
        let m = s.match(/tizen (\d+(?:\.\d+)*)/); if(m) r.osVersion = m[1]
    }
    else if(s.indexOf('webos') >= 0 || s.indexOf('web0s') >= 0){
        r.os = 'webOS'
        let m = s.match(/web0?s\/(\d+(?:\.\d+)*)/); if(m) r.osVersion = m[1]
    }
    else if(s.indexOf('cros') >= 0){ r.os = 'ChromeOS' }
    else if(s.indexOf('linux') >= 0){ r.os = 'Linux' }

    if(s.indexOf('edg/') >= 0){
        r.browser = 'Edge'
        let m = s.match(/edg\/(\d+(?:\.\d+)*)/); if(m) r.browserVersion = m[1]
    }
    else if(s.indexOf('opr/') >= 0 || s.indexOf('opera') >= 0){
        r.browser = 'Opera'
        let m = s.match(/(?:opr|opera)\/(\d+(?:\.\d+)*)/); if(m) r.browserVersion = m[1]
    }
    else if(s.indexOf('chrome/') >= 0){
        r.browser = 'Chrome'
        let m = s.match(/chrome\/(\d+(?:\.\d+)*)/); if(m) r.browserVersion = m[1]
    }
    else if(s.indexOf('firefox/') >= 0){
        r.browser = 'Firefox'
        let m = s.match(/firefox\/(\d+(?:\.\d+)*)/); if(m) r.browserVersion = m[1]
    }
    else if(s.indexOf('safari/') >= 0 && s.indexOf('version/') >= 0){
        r.browser = 'Safari'
        let m = s.match(/version\/(\d+(?:\.\d+)*)/); if(m) r.browserVersion = m[1]
    }
    else if(s.indexOf('msie') >= 0 || s.indexOf('trident') >= 0){ r.browser = 'IE' }

    return r
}

// Подробный fingerprint устройства/окружения. Используется в /session/start
// и в служебных событиях (например, plugin_button_missing) для разбора причин
// сбоев на конкретных платформах/девайсах.
function collectFingerprint(){
    let nav = navigator || {}
    let scr = (typeof screen !== 'undefined') ? screen : {}
    let conn = nav.connection || nav.mozConnection || nav.webkitConnection || {}
    let parsed = parseUserAgent(nav.userAgent || '')

    let webgl = {}
    try {
        let canvas = document.createElement('canvas')
        let gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl')
        if(gl){
            let dbg = gl.getExtension('WEBGL_debug_renderer_info')
            if(dbg){
                webgl.vendor = String(gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL) || '').slice(0, 64)
                webgl.renderer = String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) || '').slice(0, 128)
            }
        }
    } catch(e){}

    let timezone = ''
    try { timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || '' } catch(e){}

    let nativePlatform = ''
    try { nativePlatform = Platform.get() || '' } catch(e){}

    return {
        platform: platformName(),
        platformNative: nativePlatform,
        userAgent: (nav.userAgent || '').slice(0, 512),
        browser: parsed.browser,
        browserVersion: parsed.browserVersion,
        os: parsed.os,
        osVersion: parsed.osVersion,
        device: parsed.device,
        lang: (nav.language || '').slice(0, 16),
        languages: (nav.languages || []).slice(0, 5).map(l => String(l).slice(0, 16)),
        timezone: timezone,
        timezoneOffset: new Date().getTimezoneOffset(),
        screenInnerW: window.innerWidth || 0,
        screenInnerH: window.innerHeight || 0,
        screenW: scr.width || 0,
        screenH: scr.height || 0,
        colorDepth: scr.colorDepth || 0,
        pixelRatio: window.devicePixelRatio || 1,
        touch: ('ontouchstart' in window) || (nav.maxTouchPoints > 0),
        cores: nav.hardwareConcurrency || 0,
        memory: nav.deviceMemory || 0,
        connection: {
            effectiveType: conn.effectiveType || '',
            downlink: conn.downlink || 0,
            rtt: conn.rtt || 0,
            saveData: !!conn.saveData
        },
        webgl: webgl,
        cookieEnabled: !!nav.cookieEnabled,
        appVersion: Manifest.app_version || '',
        cssVersion: Manifest.css_version || ''
    }
}

function authToken(){
    try {
        let permit = (Account && Account.Permit) || {}
        return permit.token || ''
    } catch(e){ return '' }
}

function sendJson(path, payload, opts){
    opts = opts || {}
    let url = API_BASE + path
    let body = JSON.stringify(payload || {})
    let token = authToken()

    if(opts.beacon && navigator && typeof navigator.sendBeacon === 'function'){
        try {
            let blob = new Blob([body], { type: 'application/json' })
            return navigator.sendBeacon(url, blob)
        } catch(e){ /* fall through to fetch */ }
    }

    let headers = { 'Content-Type': 'application/json' }
    if(token) headers['Authorization'] = 'Bearer ' + token

    if(typeof fetch === 'function'){
        try {
            return fetch(url, {
                method: 'POST',
                headers: headers,
                body: body,
                keepalive: !!opts.keepalive,
                credentials: 'omit',
                mode: 'cors'
            }).catch(() => {})
        } catch(e){}
    }

    try {
        $.ajax({ url: url, type: 'POST', dataType: 'json', contentType: 'application/json', data: body, headers: headers })
    } catch(e){}
}

// ── Source recommendations (per card) ───────────────────────────────────────
// Тянем с бэка ранжированный по качеству просмотра список источников для
// конкретного фильма и кешируем по cardId. Онлайн-плагин (lampac) читает это
// через window.lampaSourceReco и поднимает рекомендованные источники наверх.
let recoCache    = {}
let recoInflight = {}

// Глобальный рейтинг источников (без привязки к карточке): активные пробы
// балансеров × поведение всех юзеров. Дефолтный порядок для карточек, по
// которым ещё нет своей статистики. Обновляется раз в полчаса.
const RECO_GLOBAL_TTL_MS = 30 * 60 * 1000
let recoGlobal    = null
let recoGlobalAt  = 0
let recoGlobalInflight = null

function fetchGlobalReco(){
    let now = Date.now()
    if(recoGlobal && now - recoGlobalAt < RECO_GLOBAL_TTL_MS) return Promise.resolve(recoGlobal)
    if(recoGlobalInflight) return recoGlobalInflight
    recoGlobalInflight = getJson('/recommendations/sources/global').then((res) => {
        recoGlobalInflight = null
        let ranked = (res && res.ok && Array.isArray(res.ranked)) ? res.ranked : []
        if(ranked.length){
            recoGlobal   = ranked.map((s) => String(s).toLowerCase())
            recoGlobalAt = Date.now()
        }
        return recoGlobal || []
    })
    return recoGlobalInflight
}

function getJson(path){
    return new Promise((resolve) => {
        if(!API_BASE){ resolve(null); return }
        let url = API_BASE + path
        if(typeof fetch === 'function'){
            try {
                fetch(url, { method: 'GET', credentials: 'omit', mode: 'cors' })
                    .then(r => (r && r.ok) ? r.json() : null)
                    .then(resolve)
                    .catch(() => resolve(null))
                return
            } catch(e){}
        }
        try {
            $.ajax({ url: url, type: 'GET', dataType: 'json',
                success: (d) => resolve(d), error: () => resolve(null) })
        } catch(e){ resolve(null) }
    })
}

function fetchSourceReco(cardId){
    cardId = cardId != null ? String(cardId) : ''
    if(!cardId) return Promise.resolve(null)
    if(recoCache[cardId]) return Promise.resolve(recoCache[cardId])
    if(recoInflight[cardId]) return recoInflight[cardId]
    let q = '/recommendations/sources?cardId=' + encodeURIComponent(cardId)
        + (VISITOR_UID ? '&visitorUid=' + encodeURIComponent(VISITOR_UID) : '')
    recoInflight[cardId] = getJson(q).then((res) => {
        let items  = (res && res.ok && Array.isArray(res.items))  ? res.items  : []
        let ranked = (res && res.ok && Array.isArray(res.ranked)) ? res.ranked : items.map(i => i && i.source)
        let bySource = {}
        items.forEach((it) => { if(it && it.source) bySource[String(it.source).toLowerCase()] = it })
        recoCache[cardId] = { ranked: ranked.filter(Boolean), bySource: bySource }
        delete recoInflight[cardId]
        return recoCache[cardId]
    })
    return recoInflight[cardId]
}

function readUtm(){
    try {
        let qs = (window.location.search || '').replace(/^\?/, '')
        if(!qs) return {}
        let pairs = qs.split('&')
        let out = {}
        for(let p of pairs){
            let [k, v] = p.split('=')
            if(!k) continue
            let key = decodeURIComponent(k).toLowerCase()
            let val = v ? decodeURIComponent(v) : ''
            if(key === 'utm_source')   out.source   = val
            if(key === 'utm_medium')   out.medium   = val
            if(key === 'utm_campaign') out.campaign = val
        }
        return out
    } catch(e){ return {} }
}

function startSession(){
    sendJson('/session/start', {
        sessionId: SESSION_ID,
        visitorUid: VISITOR_UID,
        platform: platformName(),
        userAgent: (navigator.userAgent || '').slice(0, 255),
        lang: (navigator.language || '').slice(0, 16),
        screenW: window.innerWidth || 0,
        screenH: window.innerHeight || 0,
        referrer: (document.referrer || '').slice(0, 512),
        utm: readUtm(),
        appVersion: Manifest.app_version || '',
        fingerprint: collectFingerprint()
    })
}

function sendHeartbeat(){
    if(!SESSION_ID) return
    sendJson('/session/heartbeat', { sessionId: SESSION_ID, visitorUid: VISITOR_UID })
}

function endSessionBeacon(reason){
    if(!SESSION_ID) return
    if(currentPlayback) endPlayback({ reason: reason || 'unload', beacon: true })
    flush({ beacon: true })
    sendJson('/session/end', { sessionId: SESSION_ID, visitorUid: VISITOR_UID, reason: reason || 'unload' }, { beacon: true })
}

function track(eventType, eventName, props){
    if(!SESSION_ID) return
    queue.push({
        eventType: String(eventType || '').slice(0, 64),
        eventName: eventName ? String(eventName).slice(0, 128) : null,
        targetType: props && props.targetType ? String(props.targetType).slice(0, 64) : null,
        targetId:   props && props.targetId   ? String(props.targetId).slice(0, 128) : null,
        properties: props && props.properties != null ? props.properties : (props ? sanitizeProps(props) : null),
        ts: Date.now()
    })
    if(queue.length >= FLUSH_BATCH_LIMIT) flush()
}

function sanitizeProps(p){
    if(!p) return null
    let { targetType, targetId, ...rest } = p
    let keys = Object.keys(rest)
    if(!keys.length) return null
    return rest
}

function flush(opts){
    opts = opts || {}
    if(!queue.length || !SESSION_ID) return
    let batch = queue.splice(0, FLUSH_BATCH_LIMIT)
    sendJson('/events', {
        sessionId: SESSION_ID,
        visitorUid: VISITOR_UID,
        events: batch
    }, { beacon: !!opts.beacon, keepalive: !!opts.beacon })
}

// ── Player tracking ─────────────────────────────────────────────────────────
function detectCardType(card){
    if(!card) return 'unknown'
    if(card.iptv) return 'iptv'
    if(card.first_air_date != null || card.name) return 'tv'
    return 'movie'
}

function detectPlayMode(data){
    if(!data) return 'online'
    if(data.iptv) return 'iptv'
    if(data.torrent_hash) return 'torrent'
    if(data.youtube) return 'youtube'
    if(data.continue_play) return 'continue'
    return 'online'
}

// lampac and other online plugins set the balancer name on different fields
// depending on version. Try the common ones in priority order so we capture
// something meaningful (e.g. "zetflix", "videohub") instead of a blank source.
//
// As a last resort we read 'online_balanser' from Lampa.Storage — the online
// plugin writes the currently selected balancer there before launching the
// player (see plugins/online/component.js).
function detectSource(data){
    if(!data) return null
    let raw = data.balanser
        || data.balanser_name
        || data.online_name
        || data.stat_from
        || null
    if(!raw && data.torrent_hash) raw = 'torrent'
    else if(!raw && data.youtube) raw = 'youtube'
    else if(!raw && data.iptv) raw = 'iptv'
    if(!raw){
        try {
            if(typeof Lampa !== 'undefined' && Lampa.Storage){
                let stored = Lampa.Storage.get('online_balanser', '')
                if(stored) raw = stored
            }
        } catch(e){}
    }
    if(!raw) return null
    return String(raw).toLowerCase().replace(/\s+/g, '_').slice(0, 64)
}

function detectVoice(data){
    if(!data) return null
    let raw = data.voice_name || data.translate || data.voice || null
    if(!raw) return null
    return String(raw).slice(0, 64)
}

// Pick a single canonical quality string. data.quality is usually an object
// like { "1080p": "...", "720p": "..." } from lampac plugins; the actually
// played one is the highest. data.quality_value / data.quality_name appear in
// some forks.
function detectQuality(data){
    if(!data) return null
    let raw = data.quality_value || data.quality_name || null
    if(!raw && data.quality){
        if(typeof data.quality === 'string') raw = data.quality
        else if(typeof data.quality === 'object'){
            let keys = Object.keys(data.quality)
            // Prefer 1080p > 720p > 480p > others; otherwise first key.
            let pref = ['2160p','4K','1080p','720p','480p','360p']
            raw = pref.find(p => keys.indexOf(p) !== -1) || keys[0] || null
        }
    }
    if(!raw) return null
    return String(raw).slice(0, 16)
}

// Last resort for card context: pull whatever the active activity holds.
// `data.card` is missing when the user launches playback from an online
// plugin that doesn't pass it; player.js itself uses the same fallback.
function getActivityCard(){
    try {
        if(typeof Lampa === 'undefined' || !Lampa.Activity) return null
        let active = Lampa.Activity.active && Lampa.Activity.active()
        if(!active) return null
        return active.movie || (active.card_data && active.card_data.movie) || null
    } catch(e){ return null }
}

function startPlayback(data){
    if(!data) return
    let card = data.card || lastOpenedCard || getActivityCard() || {}
    lastOpenedCard = null
    currentPlayback = {
        clientPlaybackId: uuid(),
        cardId: card.id != null ? String(card.id) : null,
        cardType: detectCardType(card),
        cardTitle: ((card.title || card.name || data.title || '').slice(0, 255)) || null,
        season: typeof data.season === 'number' ? data.season : null,
        episode: typeof data.episode === 'number' ? data.episode : null,
        source: detectSource(data),
        voice: detectVoice(data),
        quality: detectQuality(data),
        playMode: detectPlayMode(data),
        startedAt: new Date().toISOString(),
        watchedSeconds: 0,
        durationSeconds: null,
        lastPosition: 0,
        completed: false,
        sentStart: false,
        // QoE: время до первого кадра и буферизации/перемотки/паузы.
        // Счётчики монотонно растут — бэк мержит их через max().
        ttffMs: null,
        bufferCount: 0,
        bufferSeconds: 0,
        seekCount: 0,
        pauseCount: 0,
        _openedAt: Date.now(),
        _playingStarted: false,
        _bufferStartedAt: null
    }

    sendPlaybackUpdate({ start: true })
    track('playback_start', null, {
        targetType: 'card',
        targetId: currentPlayback.cardId,
        properties: {
            card_id: currentPlayback.cardId,
            card_type: currentPlayback.cardType,
            card_title: currentPlayback.cardTitle,
            play_mode: currentPlayback.playMode
        }
    })

    if(progressTimer) clearInterval(progressTimer)
    progressTimer = setInterval(() => {
        if(currentPlayback) sendPlaybackUpdate({})
    }, PROGRESS_MS)
}

function updatePlaybackProgress(e){
    if(!currentPlayback || !e) return
    if(typeof e.duration === 'number' && e.duration > 0) currentPlayback.durationSeconds = e.duration
    if(typeof e.current === 'number') currentPlayback.lastPosition = e.current
    if(typeof e.watched === 'number') currentPlayback.watchedSeconds = e.watched
    if(currentPlayback.durationSeconds && currentPlayback.lastPosition){
        if(currentPlayback.lastPosition / currentPlayback.durationSeconds > 0.9) currentPlayback.completed = true
    }
}

// ── QoE: TTFF / буферизации / перемотки / паузы ─────────────────────────────
// Видео реально начало играть (canplay/playing): фиксируем TTFF и закрываем
// открытый интервал буферизации.
function qoeVideoStarted(){
    if(!currentPlayback) return
    if(currentPlayback.ttffMs == null && currentPlayback._openedAt){
        currentPlayback.ttffMs = Math.max(0, Date.now() - currentPlayback._openedAt)
    }
    if(currentPlayback._bufferStartedAt){
        currentPlayback.bufferSeconds += (Date.now() - currentPlayback._bufferStartedAt) / 1000
        currentPlayback._bufferStartedAt = null
    }
    currentPlayback._playingStarted = true
}

// 'waiting' до первого кадра — это стартовая загрузка (учтена в TTFF),
// буферизацией считаем только паузы после начала воспроизведения.
function qoeVideoWaiting(){
    if(!currentPlayback || !currentPlayback._playingStarted) return
    if(currentPlayback._bufferStartedAt) return
    currentPlayback.bufferCount += 1
    currentPlayback._bufferStartedAt = Date.now()
}

function qoeCloseBuffer(){
    if(currentPlayback && currentPlayback._bufferStartedAt){
        currentPlayback.bufferSeconds += (Date.now() - currentPlayback._bufferStartedAt) / 1000
        currentPlayback._bufferStartedAt = null
    }
}

function sendPlaybackUpdate(opts){
    if(!currentPlayback) return
    let pb = currentPlayback
    let payload = {
        clientPlaybackId: pb.clientPlaybackId,
        sessionId: SESSION_ID,
        visitorUid: VISITOR_UID,
        cardId: pb.cardId,
        cardType: pb.cardType,
        cardTitle: pb.cardTitle,
        season: pb.season,
        episode: pb.episode,
        source: pb.source,
        voice: pb.voice,
        quality: pb.quality,
        playMode: pb.playMode,
        startedAt: pb.startedAt,
        watchedSeconds: Math.max(0, Math.round(pb.watchedSeconds || 0)),
        durationSeconds: pb.durationSeconds || null,
        lastPosition: pb.lastPosition || null,
        completed: !!pb.completed,
        ttffMs: pb.ttffMs != null ? Math.round(pb.ttffMs) : null,
        bufferCount: pb.bufferCount || 0,
        bufferSeconds: Math.round((pb.bufferSeconds || 0) * 10) / 10,
        seekCount: pb.seekCount || 0,
        pauseCount: pb.pauseCount || 0
    }
    if(opts && opts.end){
        payload.endedAt = new Date().toISOString()
        payload.endedReason = opts.reason || 'manual_close'
    }
    sendJson('/playbacks', payload, { beacon: !!opts.beacon, keepalive: !!opts.beacon })
}

function endPlayback(opts){
    if(!currentPlayback) return
    qoeCloseBuffer()
    sendPlaybackUpdate({ end: true, reason: opts && opts.reason, beacon: opts && opts.beacon })
    if(opts && opts.reason === 'ended') {
        track('playback_end', null, { targetType: 'card', targetId: currentPlayback.cardId })
    }
    if(progressTimer){ clearInterval(progressTimer); progressTimer = null }
    currentPlayback = null
}

// ── Public listeners wiring ─────────────────────────────────────────────────
function wireListeners(){
    // Page / activity
    Lampa.Listener.follow('activity', (e) => {
        if(e.type === 'archive') return
        if(e.type === 'start' || e.type === 'create'){
            track('page_view', e.component || (e.activity && e.activity.template), {
                targetType: 'activity',
                targetId: e.component || null
            })
        }
    })

    // Search query (debounced from search/global.js)
    Lampa.Listener.follow('search', (e) => {
        if(e && e.query) search(e.query)
    })

    // Card open
    Lampa.Listener.follow('full', (e) => {
        if(e.type !== 'complite') return
        let card = (e.data && e.data.movie) || (e.object && e.object.card) || null
        if(!card) return
        lastOpenedCard = card
        // Префетчим рекомендации источников заранее — к моменту запуска плеера
        // (выбора балансера в lampac) ответ уже будет в кеше.
        if(card.id != null){ try { fetchSourceReco(card.id) } catch(e){} }
        track('card_open', null, {
            targetType: 'card',
            targetId: card.id != null ? String(card.id) : null,
            properties: {
                card_id: card.id,
                card_type: detectCardType(card),
                card_title: (card.title || card.name || '').slice(0, 255)
            }
        })
    })

    // Player
    Lampa.Player.listener.follow('start', (data) => {
        try { startPlayback(data) } catch(e){}
    })
    Lampa.Player.listener.follow('progress', (e) => {
        try { updatePlaybackProgress(e) } catch(e){}
    })
    Lampa.Player.listener.follow('ended', () => {
        try {
            if(currentPlayback) currentPlayback.completed = true
            endPlayback({ reason: 'ended' })
        } catch(e){}
    })
    // Ошибки плеера на бэк не отправляем. Делаем обычный снапшот прогресса —
    // он не содержит признака ошибки, но фиксирует фактический досмотр, что само
    // по себе сигнал качества источника (сбойный источник = низкий watched).
    Lampa.Player.listener.follow('error', () => {
        try { if(currentPlayback) sendPlaybackUpdate({}) } catch(err){}
    })
    Lampa.Player.listener.follow('destroy', () => {
        try { endPlayback({ reason: 'manual_close' }) } catch(e){}
    })

    // QoE: события видео-слоя (PlayerVideo.listener общий для HTML5/Tizen/Orsay).
    // canplay/playing → TTFF и конец буферизации; waiting → буферизация;
    // pause/rewind → счётчики поведения внутри плеера.
    if(window.Lampa && Lampa.PlayerVideo && Lampa.PlayerVideo.listener){
        let vl = Lampa.PlayerVideo.listener
        vl.follow('canplay', () => { try { qoeVideoStarted() } catch(e){} })
        vl.follow('playing', () => { try { qoeVideoStarted() } catch(e){} })
        vl.follow('waiting', () => { try { qoeVideoWaiting() } catch(e){} })
        vl.follow('pause', () => {
            try { if(currentPlayback && currentPlayback._playingStarted) currentPlayback.pauseCount += 1 } catch(e){}
        })
        vl.follow('rewind', () => {
            try { if(currentPlayback && currentPlayback._playingStarted) currentPlayback.seekCount += 1 } catch(e){}
        })
    }

    // Bookmarks
    if(window.Lampa && Lampa.Favorite && Lampa.Favorite.listener && Lampa.Favorite.listener.follow){
        Lampa.Favorite.listener.follow('add', (e) => {
            track('bookmark_add', null, {
                targetType: 'card',
                targetId: e && e.card ? String(e.card.id || '') : null,
                properties: { where: e && e.where }
            })
        })
        Lampa.Favorite.listener.follow('remove', (e) => {
            track('bookmark_remove', null, {
                targetType: 'card',
                targetId: e && e.card ? String(e.card.id || '') : null,
                properties: { where: e && e.where }
            })
        })
    }

    // Generic state listener for clicks / favorites already broadcast via `state:changed`
    Lampa.Listener.follow('state:changed', (e) => {
        if(!e || e.target !== 'favorite') return
        if(e.method === 'add' || e.method === 'added'){
            track('bookmark_add', null, {
                targetType: 'card',
                targetId: e.card && e.card.id != null ? String(e.card.id) : null,
                properties: { where: e.type }
            })
        }
        if(e.method === 'remove'){
            track('bookmark_remove', null, {
                targetType: 'card',
                targetId: e.card && e.card.id != null ? String(e.card.id) : null,
                properties: { where: e.type }
            })
        }
    })
}

function wireDomClicks(){
    document.addEventListener('click', (ev) => {
        let el = ev.target
        if(!el || !el.closest) return
        let card = el.closest('.card, .full-start__background, [data-track-card]')
        if(card){
            let cid = card.getAttribute('data-card-id') || card.getAttribute('data-id') || ''
            track('click', 'card.click', {
                targetType: 'card',
                targetId: cid || null
            })
            return
        }
        let menuItem = el.closest('.menu__item, [data-action]')
        if(menuItem){
            let action = menuItem.getAttribute('data-action') || menuItem.className || ''
            track('click', 'menu.click', {
                targetType: 'menu_item',
                targetId: action.slice(0, 128)
            })
            return
        }
        let btn = el.closest('.simple-button, .full-start__button, .selectbox__item, button')
        if(btn){
            let label = (btn.textContent || '').trim().slice(0, 64)
            track('click', 'button.click', {
                targetType: 'button',
                targetId: label || null
            })
        }
    }, true)
}

function wireUnload(){
    window.addEventListener('pagehide',    () => endSessionBeacon('pagehide'))
    window.addEventListener('beforeunload', () => endSessionBeacon('beforeunload'))
    document.addEventListener('visibilitychange', () => {
        if(document.visibilityState === 'hidden'){
            flush({ beacon: true })
            if(currentPlayback) sendPlaybackUpdate({ beacon: true })
        } else {
            sendHeartbeat()
        }
    })
}

// ── Public entry ────────────────────────────────────────────────────────────
function search(query){
    if(!query || typeof query !== 'string') return
    let q = query.trim()
    if(!q) return
    track('search', null, {
        targetType: 'search',
        targetId: q.slice(0, 128),
        properties: { query: q.slice(0, 128) }
    })
}

function init(){
    if(inited) return
    inited = true

    if(!Storage.get('lampa_uid','')) Storage.set('lampa_uid', Utils.uid())
    VISITOR_UID = Storage.get('lampa_uid', '')
    SESSION_ID  = uuid()

    let host = (Manifest.cub_site || '').replace(/\/+$/,'')
    if(!host){ console.log('Analytics','no cub_site configured, disabled'); return }
    API_BASE = Utils.protocol() + host + '/api/analytics'

    // Глобальный хук для онлайн-плагина (lampac): он не импортирует наши модули,
    // поэтому отдаём рекомендации источников через window.
    //   cached(cardId) — синхронно: ранжированный список ключей источников (или [])
    //   stats(cardId)  — синхронно: карта source(lowercase) → {viewers,score,...}
    //   prefetch/get(cardId) — асинхронно загрузить и закешировать
    try {
        let entry = (cardId) => recoCache[cardId != null ? String(cardId) : ''] || null
        window.lampaSourceReco = {
            cached:   (cardId) => { let e = entry(cardId); return (e && e.ranked) || [] },
            stats:    (cardId) => { let e = entry(cardId); return (e && e.bySource) || {} },
            prefetch: (cardId) => { fetchSourceReco(cardId) },
            get:      (cardId) => fetchSourceReco(cardId),
            // Глобальный порядок источников — синхронно из кеша (плагин зовёт его
            // в момент сборки списка балансеров) + фоновое обновление.
            globalCached: () => recoGlobal || [],
            globalPrefetch: () => { fetchGlobalReco() }
        }
    } catch(e){}

    try {
        startSession()
        wireListeners()
        wireDomClicks()
        wireUnload()
        hbTimer    = setInterval(sendHeartbeat, HEARTBEAT_MS)
        flushTimer = setInterval(flush, FLUSH_INTERVAL_MS)
        // Глобальный рейтинг источников — заранее, чтобы был в кеше к первому
        // открытию плеера (плагин читает его синхронно).
        try { fetchGlobalReco() } catch(e){}
    } catch(e){
        console.log('Analytics','init failed', e)
    }
}

export default {
    init,
    track,
    search,
    fingerprint: collectFingerprint
}
